import React, { type ReactNode, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import PullRequestIcon from '@atlaskit/icon/core/pull-request';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';
import type { IssueLinkedPRsEntry } from '@atlassian/search-client/hydration/pr-types';
import { type JiraStatusCategoryColorType } from '@atlassian/search-client/hydration/types';
import { getJiraUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { Expand } from '../../../../../../common/ui/expand-list';
import { Assignee as AssigneeComponent } from '../../../../../../common/ui/jira-assignee';
import { getStatus } from '../../../../../../common/ui/jira-status';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getLinksFromText } from '../../../../../../common/ui/link-attachments';
import { LinkedPullRequestsContent } from '../../../../../../common/ui/linked-pull-requests';
import { messages as prMessages } from '../../../../../../common/ui/linked-pull-requests/messages';
import { MentionsLinkedResults } from '../../../../../../common/ui/linked-results';
import { getJiraEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchMetadata } from '../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { messages as searchResultTypeMessages } from '../../../../../../common/ui/search-result-type/messages';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt, processExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { useCrossAppLinkHandlers } from '../../../../../../common/utils/use-cross-app-link-handlers';
import { useSearchCrossProductUrlWrapper } from '../../../../../../common/utils/use-cross-product-url-wrapper';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import { useFeatures } from '../../../../../../controllers/store/bootstrap';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchJiraIssue_AtlassianSearchPage$key } from './__generated__/searchJiraIssue_AtlassianSearchPage.graphql';
import { ProjectMetadata } from './project-metadata';
import { SearchJiraIssueTableRow } from './table-row';

export type SearchJiraIssueProps = SearchResultProps<searchJiraIssue_AtlassianSearchPage$key> & {
	jiraIssueStatus?: JiraIssueStatusAndColorName | null;
	jiraLinkedPRs?: IssueLinkedPRsEntry;
	grouping3pCohort?: string;
	/**
	 * When true, render the Jira issue table-row variant instead of the
	 * standard SearchResult card. Controlled exclusively by the parent list
	 * renderer (SearchResultsListLiteRenderer) — leaf renderers MUST NOT
	 * independently read feature flags to flip into the table form, otherwise
	 * the table row leaks onto the "All" tab whenever the lite-mode bootstrap
	 * flag is on.
	 */
	shouldRenderTableResult?: boolean;
};

export const SearchJiraIssue: ({
	node,
	position,
	cloudId,
	searchQuery,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	jiraIssueStatus,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	productConfigs,
	shouldRenderTableResult,
}: SearchJiraIssueProps) => React.JSX.Element = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	cloudId,
	searchQuery,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	jiraIssueStatus,
	jiraLinkedPRs,
	thirdPartyConfigs,
	mainContentWidth,
	selectedSites,
	productConfigs,
	shouldRenderTableResult = false,
}: SearchJiraIssueProps & ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchJiraIssue_AtlassianSearchPage on SearchResultJiraIssue
			@argumentDefinitions(
				includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }
				includeFastJiraHydration: { type: "Boolean", defaultValue: false }
			) {
				id
				title
				type
				description
				url
				lastModifiedDate
				iconUrl
				issue {
					# Legacy issue-type avatar URL — only fetched when the fast path is OFF. In the
					# fast path we read the avatar from issueType.avatar.medium instead (below).
					issueTypeAvatarUrl @skip(if: $includeFastJiraHydration)
					webUrl
					key
					# Fast (value-field) Jira hydration — gated behind the rovo_fps_jira_fast_hydration
					# experiment. When the experiment is on, ONLY these lighter fields are executed
					# (the *Field variants below are @skip-ed), so the gira hydration does less work.
					# See go/jira-issue-value-field-deprecated.
					#
					# The fast fields live inside a single inline fragment gated by
					# $includeFastJiraHydration. Priority/labels are lite-mode-only metadata, so they
					# additionally carry @include(if: $includeJiraLiteModeFields) — GraphQL ANDs the
					# nested include with the enclosing one, so they are only fetched when the
					# experiment is on AND lite mode is on. No derived variable needed.
					... @include(if: $includeFastJiraHydration) {
						issueType {
							name
							# Fast-path issue-type avatar (replaces the legacy issueTypeAvatarUrl).
							avatar {
								medium
							}
						}
						project {
							id
							name
						}
						assignee {
							name
							picture
						}
						fastPriority: priority @include(if: $includeJiraLiteModeFields) {
							iconUrl
							name
						}
						fastLabels: labels(first: 5) @include(if: $includeJiraLiteModeFields) {
							edges {
								node {
									name
								}
							}
						}
					}
					# Legacy (*Field) Jira hydration — used when the experiment is off. These are
					# @skip-ed when the fast path is on so they are not resolved by gira.
					issueTypeField @skip(if: $includeFastJiraHydration) {
						issueType {
							name
						}
					}
					fieldsById(ids: ["project"]) @skip(if: $includeFastJiraHydration) {
						edges {
							node {
								__typename
								name
								... on JiraProjectField {
									project {
										id
										name
									}
								}
							}
						}
					}
					assigneeField @skip(if: $includeFastJiraHydration) {
						user {
							name
							picture
						}
					}
					priorityField
						@include(if: $includeJiraLiteModeFields)
						@skip(if: $includeFastJiraHydration) {
						priority {
							iconUrl
							name
						}
					}
					labelsField
						@include(if: $includeJiraLiteModeFields)
						@skip(if: $includeFastJiraHydration) {
						selectedLabelsConnection(first: 5) {
							edges {
								node {
									name
								}
							}
						}
					}
				}
				__typename
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type, cloudId, selectedSites]);
	const [features] = useFeatures();

	// Legacy path: project is nested inside the `fieldsById` connection and must be
	// scanned out. Skipped entirely when the fast hydration experiment is on.
	const legacyProjectField = useMemo(() => {
		if (!data.issue?.fieldsById?.edges) {
			return;
		}
		const fields = data?.issue?.fieldsById?.edges ?? [];
		for (const field of fields) {
			if (
				field &&
				field.node?.__typename === 'JiraProjectField' &&
				field.node?.name === 'Project'
			) {
				return field.node;
			}
		}
	}, [data.issue?.fieldsById?.edges]);

	// Prefer the fast `project` value field (experiment on); fall back to the legacy
	// `fieldsById`-derived project (experiment off).
	const projectName = data.issue?.project?.name || legacyProjectField?.project?.name || '';
	const projectId: string | undefined = data.issue?.project?.id ?? legacyProjectField?.project?.id;

	const url = useMemo(() => {
		const url = data.issue?.webUrl || data.url;
		return getJiraUrlWithAnalyticsQueryParams({
			url: url,
			searchSessionId,
			resultId: data.id,
			projectId,
		});
	}, [data.issue?.webUrl, data.url, data.id, searchSessionId, projectId]);

	const wrapCrossProductUrl = useSearchCrossProductUrlWrapper({
		bridge: 'searchPage',
		primaryProduct,
		primarySubProduct,
	});
	const tableRowUrl = useMemo(
		() => wrapCrossProductUrl(url, ProductKeys.Jira) ?? url,
		[wrapCrossProductUrl, url],
	);
	// Reports the cross-app interaction using the `xpis` param already on
	// `tableRowUrl`. Table rows bypass `SearchResult`, so this surface wires it
	// itself; a no-op when the URL was not wrapped.
	const crossAppLinkHandlers = useCrossAppLinkHandlers({
		href: tableRowUrl,
		destinationApp: ProductKeys.Jira,
	});

	const enrichedTitle = useMemo(() => {
		return getTitleWithKeyAndHighlights(searchQuery, data.title, data.issue?.key);
	}, [data.title, data.issue?.key, searchQuery]);

	const enrichedDescription = useMemo(() => {
		return processExcerpt(data.description);
	}, [data.description]);

	const issueType = useMemo(() => {
		// Prefer the fast `issueType` value field (experiment on); fall back to the legacy
		// `issueTypeField.issueType` (experiment off).
		const resolvedType = (
			data.issue?.issueType?.name ?? data.issue?.issueTypeField?.issueType?.name
		)?.toLowerCase();
		if (resolvedType && resolvedType in searchResultTypeMessages) {
			return resolvedType;
		}
		return data.type;
	}, [data.type, data.issue?.issueType?.name, data.issue?.issueTypeField?.issueType?.name]);

	// Resolve the issue-type avatar URL. Prefer the fast `issueType.avatar.medium` value field
	// (experiment on); fall back to the legacy `issueTypeAvatarUrl` (experiment off / @skip-ped).
	const issueTypeAvatarUrl = useMemo(
		() => data.issue?.issueType?.avatar?.medium ?? data.issue?.issueTypeAvatarUrl ?? undefined,
		[data.issue?.issueType?.avatar?.medium, data.issue?.issueTypeAvatarUrl],
	);

	// Prefer the fast `assignee` value field (User directly); fall back to the legacy
	// `assigneeField.user`.
	//
	// We distinguish `undefined` (field not fetched — fast path OFF, or @skip-ped) from `null`
	// (field fetched but the issue is genuinely unassigned). When the fast path is ON and the
	// issue is unassigned, `data.issue.assignee` is `null`, and we must NOT fall through to the
	// legacy `assigneeField` (which is @skip-ped and therefore `undefined`). Guarding on
	// `!== undefined` makes this explicit rather than relying on the @skip directive.
	const assigneeUser = useMemo(() => {
		const fast = data.issue?.assignee;
		if (fast !== undefined) {
			// Fast path active: `null` means unassigned.
			return fast ? { name: fast.name, picture: fast.picture } : null;
		}
		const legacy = data.issue?.assigneeField?.user;
		return legacy ? { name: legacy.name, picture: legacy.picture } : null;
	}, [data.issue?.assignee, data.issue?.assigneeField?.user]);

	const metadataParts: [string, ReactNode][] = useMemo(() => {
		const parts = [
			['type', <SearchResultType type={issueType as SearchClientResultType} />],
			projectName ? ['project', <ProjectMetadata projectName={projectName} />] : null,
			[
				'lastModified',
				data.lastModifiedDate ? <LastModified lastModified={data.lastModifiedDate} /> : null,
			],
			['assignee', <AssigneeComponent user={assigneeUser} />],
		];

		return parts.filter(Boolean) as [string, ReactNode][];
	}, [issueType, data.lastModifiedDate, projectName, assigneeUser]);

	const titleMetadataParts = useMemo<[string, ReactNode][]>(() => {
		const statusInfo = { statusCategoryId: jiraIssueStatus?.statusCategoryId };
		const status =
			jiraIssueStatus !== null &&
			getStatus(jiraIssueStatus?.statusName, statusInfo, !jiraIssueStatus);
		return status ? [['status', status]] : [];
	}, [jiraIssueStatus]);

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	// PRs are gated upstream via shouldHydrateJiraGitHubPRs in SearchResultsBody.
	// jiraLinkedPRs is only populated when the user has GitHub connected, so we
	// use it directly here. The PR section is hidden entirely when there are no
	// PRs (see hasLinkedPRs / hasRelatedLinks below) to avoid empty renders.
	const effectivePRs = jiraLinkedPRs?.status === 'loaded' ? jiraLinkedPRs.prs : [];
	const hasMorePRs = jiraLinkedPRs?.status === 'loaded' ? jiraLinkedPRs.hasMore : false;

	const extractedLinksFromText = getLinksFromText(data.description);
	const hasExtractedLinks = extractedLinksFromText && extractedLinksFromText.length > 0;
	const { formatMessage } = useIntl();
	const hasLinkedPRs = effectivePRs.length > 0;
	const hasRelatedLinks =
		(linkedResults && linkedResults.length > 0) || hasExtractedLinks || hasLinkedPRs;

	// when in lite mode, we want to try and use the icon from the issue.issueTypeAvatar first and then default to .iconUrl if it is null
	const iconUrlSource = features?.lite?.enabled
		? issueTypeAvatarUrl || data.iconUrl
		: data.iconUrl || issueTypeAvatarUrl;

	const icon = getJiraEntityIcon({
		productConfigs,
		iconUrl: iconUrlSource,
		type: data.type,
		isIssue: true,
		size: 'medium',
	});

	if (shouldRenderTableResult) {
		return (
			<ResultAnalytics
				preparedResultAttributes={preparedResultAttributes}
				intentDetectionTriggered={intentDetectionTriggered}
				metadata={metadata}
				position={position}
			>
				{({ onResultClicked, onResultContextMenu: onResultCtxMenuClicked }) => (
					<SearchJiraIssueTableRow
						productConfigs={productConfigs}
						iconUrl={issueTypeAvatarUrl || data.iconUrl}
						type={data.type}
						issueKey={data.issue?.key}
						url={tableRowUrl}
						crossAppLinkHandlers={crossAppLinkHandlers}
						title={data.title}
						jiraIssueStatus={jiraIssueStatus}
						assignee={assigneeUser}
						priority={data.issue?.fastPriority ?? data.issue?.priorityField?.priority ?? null}
						labels={
							(
								data.issue?.fastLabels?.edges ??
								data.issue?.labelsField?.selectedLabelsConnection?.edges
							)?.flatMap((e) => (e?.node?.name ? [e.node.name] : [])) ?? []
						}
						// TODO: properly wire up analytics
						onClick={() => onResultClicked({})}
						onContextMenu={() => onResultCtxMenuClicked({})}
					/>
				)}
			</ResultAnalytics>
		);
	}

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					productConfigs={productConfigs}
					{...data}
					product={ProductKeys.Jira}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					url={url}
					icon={icon}
					title={enrichedTitle}
					headerMetadataParts={metadataParts}
					titleMetadata={
						<SearchMetadata productConfigs={productConfigs} parts={titleMetadataParts} />
					}
					description={<DescriptionBody description={enrichedDescription} />}
					cloudId={cloudId}
					position={position}
					partial={data}
					appMode={appMode}
					hasRelatedLinks={hasRelatedLinks}
					footer={
						<MentionsLinkedResults
							productConfigs={productConfigs}
							linkedResults={linkedResults || []}
							thirdPartyConfigs={thirdPartyConfigs}
							partial={data}
							url={url}
							position={position}
							onResultClicked={onResultClicked}
							onResultContextMenu={onResultCtxMenuClicked}
							isAllProductSelected={!selectedProduct}
							additionalExpands={
								hasLinkedPRs ? (
									<Expand
										icon={PullRequestIcon}
										attachmentCount={effectivePRs.length}
										attachmentType="pull-request"
										label={formatMessage(prMessages.expandLabel, {
											count: effectivePRs.length,
											hasMore: hasMorePRs,
										})}
									>
										<LinkedPullRequestsContent
											pullRequests={effectivePRs}
											onResultClicked={() => onResultClicked({})}
										/>
									</Expand>
								) : undefined
							}
						/>
					}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};

export const isSearchJiraIssue: (node?: any) => node is searchJiraIssue_AtlassianSearchPage$key = (
	node?: any,
): node is searchJiraIssue_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultJiraIssue';
};

export type JiraIssueStatusAndColorName = {
	statusName?: string;
	colorName?: JiraStatusCategoryColorType;
	statusCategoryId?: string;
};

export const getTitleWithKeyAndHighlights: (
	searchQuery: string,
	title: string,
	issueKey?: string,
) => React.JSX.Element | React.JSX.Element[] = (
	searchQuery: string,
	title: string,
	issueKey?: string,
) => {
	const enrichedTitle = enrichExcerpt(title);

	// If there is no issueKey or the issue key is already prefixed, simply return the title
	if (!issueKey || title.startsWith(issueKey)) {
		return enrichedTitle;
	}

	// If the issueKey exists in its entirety in the searchQuery, highlight and prefix it
	if (searchQuery.split(' ').includes(issueKey)) {
		const { highlightedTitle: highlightedIssueKey } = highlightMatchedText(
			issueKey,
			searchQuery.split(' '),
		);
		return (
			<>
				{enrichExcerpt(highlightedIssueKey)}: {enrichedTitle}
			</>
		);
	}

	// Otherwise, return the issue key unhighlighted and prefixed
	return (
		<>
			{issueKey}: {enrichedTitle}
		</>
	);
};
